import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { userPreferencesService } from '@/services/userPreferences'
import { logError } from '@/utils/logger'
import { useAuthStore } from '@/stores/auth'
import {
  type ThemeLayout,
  type ThemeMode,
  GUEST_STORAGE_KEY,
  LEGACY_STORAGE_KEY,
  userStorageKey,
  normalizeThemeMode,
  resolveIsDark,
  resolveDomIsDark,
  getSystemPrefersDark,
  readStoredPreference,
  applyDarkClass
} from '@/utils/theme'

const THEME_CYCLE: ThemeMode[] = ['light', 'dark', 'system']

export const useThemeStore = defineStore('theme', () => {
  const themeMode = ref<ThemeMode>('system')
  const systemPrefersDark = ref(getSystemPrefersDark())
  const isChangingTheme = ref(false)
  const currentLayout = ref<ThemeLayout>('app')

  const isDarkMode = computed(() => resolveIsDark(themeMode.value, systemPrefersDark.value))

  const domIsDark = computed(() =>
    resolveDomIsDark(currentLayout.value, themeMode.value, systemPrefersDark.value)
  )

  let colorSchemeMediaQuery: MediaQueryList | null = null
  let colorSchemeChangeHandler: ((event: MediaQueryListEvent) => void) | null = null

  const isUserAuthenticated = () => {
    const authStore = useAuthStore()
    return authStore.isAuthenticated
  }

  const getAuthenticatedUserId = (): number | null => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id
    return userId != null ? userId : null
  }

  const getStorageKey = (): string => {
    const userId = getAuthenticatedUserId()
    if (isUserAuthenticated() && userId != null) {
      return userStorageKey(userId)
    }
    return GUEST_STORAGE_KEY
  }

  const readLocalThemePreference = (): ThemeMode | null => {
    const scoped = readStoredPreference(getStorageKey())
    if (scoped) return scoped

    if (isUserAuthenticated()) {
      return readStoredPreference(GUEST_STORAGE_KEY)
    }

    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY)
    if (!legacy) return null
    return normalizeThemeMode(legacy)
  }

  const writeLocalThemePreference = (themeValue: ThemeMode) => {
    localStorage.setItem(getStorageKey(), themeValue)
    if (getStorageKey() !== GUEST_STORAGE_KEY) {
      localStorage.removeItem(LEGACY_STORAGE_KEY)
    }
  }

  const teardownSystemListener = () => {
    if (colorSchemeMediaQuery && colorSchemeChangeHandler) {
      colorSchemeMediaQuery.removeEventListener('change', colorSchemeChangeHandler)
    }
    colorSchemeMediaQuery = null
    colorSchemeChangeHandler = null
  }

  const refreshSystemPreference = () => {
    systemPrefersDark.value = getSystemPrefersDark()
  }

  const setupSystemListener = () => {
    teardownSystemListener()
    if (themeMode.value !== 'system' || typeof window === 'undefined') {
      return
    }

    refreshSystemPreference()
    colorSchemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    colorSchemeChangeHandler = (event) => {
      systemPrefersDark.value = event.matches
    }
    colorSchemeMediaQuery.addEventListener('change', colorSchemeChangeHandler)
  }

  const applyThemeToDocument = () => {
    applyDarkClass(domIsDark.value)
  }

  const applyThemeContext = (layout: ThemeLayout) => {
    currentLayout.value = layout
    applyThemeToDocument()
  }

  const saveThemePreference = async (themeValue: ThemeMode) => {
    try {
      writeLocalThemePreference(themeValue)

      if (!isUserAuthenticated()) {
        return
      }

      const authStore = useAuthStore()
      if (!authStore.accessToken) {
        return
      }

      await userPreferencesService.upsertPreference('theme', themeValue)
    } catch (error) {
      logError('Erreur lors de la sauvegarde de la préférence de thème:', error)
      writeLocalThemePreference(themeValue)
    }
  }

  const migrateGuestPreferenceToDatabase = async (themeValue: ThemeMode) => {
    if (!isUserAuthenticated()) return

    try {
      await userPreferencesService.upsertPreference('theme', themeValue)
    } catch (error) {
      logError('Erreur lors de la migration de la préférence de thème:', error)
    }
  }

  const setThemeMode = (mode: ThemeMode | unknown) => {
    themeMode.value = normalizeThemeMode(mode)
    if (themeMode.value === 'system') {
      refreshSystemPreference()
    }
    setupSystemListener()
    applyThemeToDocument()
  }

  const initializeTheme = async () => {
    try {
      let themePreference = readLocalThemePreference()

      const authStore = useAuthStore()
      const hasToken = !!(
        authStore.accessToken ||
        localStorage.getItem('access_token') ||
        localStorage.getItem('accessToken')
      )

      if (isUserAuthenticated() && hasToken) {
        try {
          userPreferencesService.clearCache()
          const preference = await userPreferencesService.getPreference('theme')
          if (
            preference &&
            preference.preference_value !== undefined &&
            preference.preference_value !== null
          ) {
            themePreference = normalizeThemeMode(preference.preference_value)
            writeLocalThemePreference(themePreference)
          } else if (themePreference) {
            await migrateGuestPreferenceToDatabase(themePreference)
          }
        } catch (dbError) {
          logError('Erreur lors de la récupération depuis la base de données:', dbError)
        }
      }

      if (!themePreference) {
        const legacyTheme = localStorage.getItem(LEGACY_STORAGE_KEY)
        if (legacyTheme) {
          themePreference = normalizeThemeMode(legacyTheme)
          writeLocalThemePreference(themePreference)
          localStorage.removeItem(LEGACY_STORAGE_KEY)
        }
      }

      if (themePreference) {
        setThemeMode(themePreference)
      } else {
        setThemeMode('system')
        writeLocalThemePreference('system')
        if (isUserAuthenticated()) {
          await saveThemePreference('system')
        }
      }

      applyThemeToDocument()
    } catch (error) {
      logError("Erreur lors de l'initialisation du thème:", error)
      setThemeMode('system')
      applyThemeToDocument()
    }
  }

  const cycleThemeMode = async () => {
    if (isChangingTheme.value) return

    isChangingTheme.value = true

    const currentIndex = THEME_CYCLE.indexOf(themeMode.value)
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % THEME_CYCLE.length
    const nextMode = THEME_CYCLE[nextIndex]

    setThemeMode(nextMode)
    await saveThemePreference(nextMode)
    applyThemeToDocument()

    setTimeout(() => {
      isChangingTheme.value = false
    }, 1000)
  }

  function $reset() {
    const currentTheme = normalizeThemeMode(readLocalThemePreference() || 'system')
    isChangingTheme.value = false
    setThemeMode(currentTheme)
    applyThemeToDocument()
  }

  watch(domIsDark, () => {
    applyThemeToDocument()
  })

  watch(themeMode, () => {
    setupSystemListener()
  })

  watch(
    () => isUserAuthenticated(),
    async (authenticated) => {
      if (authenticated) {
        await initializeTheme()
      } else {
        userPreferencesService.clearCache()
        applyThemeToDocument()
      }
    }
  )

  return {
    themeMode,
    isDarkMode,
    domIsDark,
    currentLayout,
    isChangingTheme,
    initializeTheme,
    cycleThemeMode,
    applyThemeContext,
    $reset
  }
})
